import type { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirm } from '@/shared/hooks';
import { useToast } from '@/shared/hooks/useToast';
import { useProjectFileMutations } from './useProjectFileMutations';
import { useProjectFolderMutations } from './useProjectFolderMutations';
import type {
  CreateProjectFolderInput,
  RenameProjectFolderInput,
  RenameProjectFileInput,
  FolderDialogState,
  FileDialogState,
} from '../types';

interface Options {
  projectId: string;
  activeFolderId: string | null;
  setActiveFolderId: Dispatch<SetStateAction<string | null>>;
  folderDialog: FolderDialogState | null;
  setFolderDialog: Dispatch<SetStateAction<FolderDialogState | null>>;
  movingFolderId: string | null;
  setMovingFolderId: Dispatch<SetStateAction<string | null>>;
  fileDialog: FileDialogState | null;
  setFileDialog: Dispatch<SetStateAction<FileDialogState | null>>;
}

export function useProjectFilesActions({
  projectId,
  activeFolderId,
  setActiveFolderId,
  folderDialog,
  setFolderDialog,
  movingFolderId,
  setMovingFolderId,
  fileDialog,
  setFileDialog,
}: Options) {
  const { t } = useTranslation('projectFiles');
  const { toast } = useToast();
  const {
    confirm,
    isOpen: confirmOpen,
    options: confirmOptions,
    handleConfirm,
    handleCancel,
  } = useConfirm();
  const fileMutations = useProjectFileMutations(projectId);
  const folderMutations = useProjectFolderMutations(projectId);

  async function handleDeleteFile(fileId: string) {
    const { confirmed } = await confirm({
      title: t('file.deleteConfirmTitle'),
      description: t('file.deleteConfirmDescription'),
      variant: 'destructive',
    });
    if (!confirmed) return;
    try {
      await fileMutations.remove.mutateAsync(fileId);
    } catch {
      // handled by global error handler
    }
  }

  async function handleMoveFile(fileId: string, folderId: string | null) {
    try {
      await fileMutations.move.mutateAsync({ fileId, data: { newFolderId: folderId } });
    } catch {
      // handled by global error handler
    }
  }

  async function handleUpload(file: File) {
    try {
      await fileMutations.upload.mutateAsync({ file, folderId: activeFolderId ?? undefined });
    } catch {
      // handled by global error handler
    }
  }

  async function handleCreateFolder(data: CreateProjectFolderInput) {
    try {
      await folderMutations.create.mutateAsync(data);
      setFolderDialog(null);
    } catch {
      // handled by global error handler
    }
  }

  async function handleRenameFolder(data: RenameProjectFolderInput) {
    if (folderDialog?.mode !== 'rename') return;
    try {
      await folderMutations.rename.mutateAsync({ folderId: folderDialog.folderId, data });
      setFolderDialog(null);
    } catch {
      // handled by global error handler
    }
  }

  async function handleBulkDeleteFiles(ids: Set<string>) {
    const count = ids.size;
    const { confirmed } = await confirm({
      title: t('file.bulkDeleteConfirmTitle', { count }),
      description: t('file.bulkDeleteConfirmDescription'),
      variant: 'destructive',
    });
    if (!confirmed) return;
    try {
      const response = await fileMutations.bulkDelete.mutateAsync([...ids]);
      if (response.data.failed.length > 0) {
        toast.warning({
          title: t('file.bulkDeletePartialWarning', { count: response.data.failed.length }),
        });
      }
    } catch {
      // handled by global error handler
    }
  }

  async function handleDeleteFolder(folderId: string) {
    const { confirmed } = await confirm({
      title: t('folder.deleteConfirmTitle'),
      description: t('folder.deleteConfirmDescription'),
      variant: 'destructive',
    });
    if (!confirmed) return;
    try {
      await folderMutations.remove.mutateAsync(folderId);
      if (activeFolderId === folderId) setActiveFolderId(null);
    } catch {
      // handled by global error handler
    }
  }

  function handleMoveFolder(folderId: string) {
    setMovingFolderId(folderId);
  }

  async function handleMoveFolderConfirm(newParentFolderId: string | null) {
    if (!movingFolderId) return;
    try {
      await folderMutations.move.mutateAsync({
        folderId: movingFolderId,
        data: { newParentFolderId },
      });
      setMovingFolderId(null);
    } catch {
      // handled by global error handler
    }
  }

  function handleOpenRenameFile(fileId: string, currentName: string) {
    setFileDialog({ mode: 'rename', fileId, currentName });
  }

  async function handleRenameFileConfirm(data: RenameProjectFileInput) {
    if (fileDialog?.mode !== 'rename') return;
    try {
      await fileMutations.renameFile.mutateAsync({ fileId: fileDialog.fileId, data });
      setFileDialog(null);
    } catch {
      // handled by global error handler
    }
  }

  async function handleToggleFavorite(fileId: string, isFavorite: boolean) {
    try {
      await fileMutations.toggleFavorite.mutateAsync({ fileId, isFavorite });
    } catch {
      // handled by global error handler
    }
  }

  async function handleToggleVisibility(fileId: string, isPrivate: boolean) {
    try {
      await fileMutations.setVisibility.mutateAsync({ fileId, isPrivate });
    } catch {
      // handled by global error handler
    }
  }

  return {
    fileMutations,
    confirmOpen,
    confirmOptions,
    handleConfirm,
    handleCancel,
    handleDeleteFile,
    handleBulkDeleteFiles,
    handleMoveFile,
    handleUpload,
    handleCreateFolder,
    handleRenameFolder,
    handleDeleteFolder,
    handleMoveFolder,
    handleMoveFolderConfirm,
    handleOpenRenameFile,
    handleRenameFileConfirm,
    handleToggleFavorite,
    handleToggleVisibility,
  };
}
